/**
 * Is this a real GPS fix, or an absent one wearing a zero?
 *
 * THE BUG THIS EXISTS TO PREVENT: `Number(null)` is `0`, and
 * `Number.isFinite(0)` is `true`. So the obvious check —
 *
 *     Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
 *
 * — accepts a driver who has no location at all and places them at 0°N 0°E,
 * a point in the Gulf of Guinea off West Africa. A hauler opening their driver
 * map sees a truck in the middle of the ocean, and any ETA computed from it
 * reads in the thousands of minutes because it is measuring the great-circle
 * distance from Null Island to the Permian Basin.
 *
 * `Number('')` is also `0`, so an empty string from a form or a query param
 * lands in the same place.
 *
 * This was found and fixed once already inside the waste-management route, and
 * the client kept its own weaker copy of the check — which is how a truck ended
 * up in the Atlantic on the hauler's screen. One implementation now, imported by
 * both sides, so the next person cannot fix half of it.
 */

/** A coordinate, or null. Never a zero standing in for "missing". */
export function parseGpsCoord(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Real field GPS only: rejects null/empty, out-of-range, and Null Island.
 *
 * The 1e-5 window is about 1.1 metres at the equator. Nothing in the Permian
 * Basin is within a metre of (0,0), so treating that as "no fix" costs nothing
 * real and catches every default-zero row.
 */
export function isUsableGps(lat: unknown, lng: unknown): boolean {
  const la = parseGpsCoord(lat);
  const ln = parseGpsCoord(lng);
  if (la == null || ln == null) return false;
  if (Math.abs(la) > 90 || Math.abs(ln) > 180) return false;
  if (Math.abs(la) < 1e-5 && Math.abs(ln) < 1e-5) return false;
  return true;
}

/**
 * The pair as numbers, or null if either side is unusable.
 *
 * Callers that need both values get them without re-parsing, and — more to the
 * point — without being able to forget the guard and reach for `Number(lat)`
 * again.
 */
export function usableGpsPair(
  lat: unknown,
  lng: unknown,
): { lat: number; lng: number } | null {
  if (!isUsableGps(lat, lng)) return null;
  return { lat: parseGpsCoord(lat) as number, lng: parseGpsCoord(lng) as number };
}
